import { Suspense, useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { RouteFallback } from '@/components/common/RouteFallback';
import {
  LayoutDashboard,
  ClipboardCheck,
  Mail,
  Stethoscope,
  Briefcase,
  Building2,
  ShieldCheck,
  ShieldOff,
  ListChecks,
  Settings as SettingsIcon,
  ScrollText,
  Users2,
  UserCheck,
  LogOut,
  Menu,
  X,
  type LucideIcon,
} from 'lucide-react';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { RoleSwitcher } from '@/components/common/RoleSwitcher';
import { NotificationBell } from '@/components/common/NotificationBell';
import { useAuth } from '@/providers/AuthProvider';
import { cn } from '@/lib/cn';

interface NavItem { to: string; label: string; Icon: LucideIcon; roles?: string[] }

const NAV: NavItem[] = [
  { to: '/admin/dashboard',            label: 'Dashboard',             Icon: LayoutDashboard },
  { to: '/admin/applications',         label: 'Candidate assessment',  Icon: ClipboardCheck },
  { to: '/admin/offers',               label: 'Offer management',      Icon: Mail },
  { to: '/admin/doctors',              label: 'Doctor management',     Icon: Stethoscope },
  { to: '/admin/jobs',                 label: 'Job entry',             Icon: Briefcase },
  { to: '/admin/hospitals',            label: 'Hospitals',             Icon: Building2 },
  { to: '/admin/compliance',           label: 'Compliance verification',Icon: ShieldCheck },
  { to: '/admin/referees',             label: 'Reference checks',      Icon: UserCheck },
  { to: '/admin/checklist-documents',  label: 'Checklist documents',   Icon: ListChecks },
  { to: '/admin/audit',                label: 'Audit trail',           Icon: ScrollText },
  { to: '/admin/team',                 label: 'Team management',       Icon: Users2 },
  { to: '/admin/erasure-requests',     label: 'Erasure queue',         Icon: ShieldOff },
  { to: '/admin/settings',             label: 'Settings',              Icon: SettingsIcon },
];

export function AdminLayout() {
  const [open, setOpen] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex bg-bg">
      {open && (
        <div className="lg:hidden fixed inset-0 z-40 bg-ink/40" onClick={() => setOpen(false)} />
      )}

      {/*
        The admin sidebar uses theme-INDEPENDENT tokens (`bg-admin-surface`,
        `text-admin-ink`) so it stays dark in both light and dark mode —
        matching the established convention for admin consoles
        (Linear / Vercel / Supabase Studio).
      */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-72 transform bg-admin-surface text-admin-ink transition-transform lg:static lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex h-16 items-center justify-between px-5 border-b border-admin-border">
          <span className="inline-flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-ink shadow-sm">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <path d="M12 2 L12 22" />
                <path d="M5 9 L19 9" />
                <path d="M5 9 C5 13.5 8 17 12 22 C16 17 19 13.5 19 9" />
              </svg>
            </span>
            <span className="font-display text-lg font-bold text-admin-ink">
              Locum<span className="text-primary">Network</span>
            </span>
          </span>
          <button
            className="lg:hidden rounded-lg p-1.5 text-admin-ink-muted hover:bg-admin-elevated"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-admin-border">
          <Badge tone="accent">Admin console</Badge>
        </div>

        <nav className="flex flex-col gap-0.5 p-3">
          {NAV.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/admin/dashboard'}
              className={({ isActive }) =>
                cn(
                  'group flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition',
                  isActive
                    ? 'bg-primary/15 text-admin-ink ring-1 ring-primary/30'
                    : 'text-admin-ink-muted hover:bg-admin-elevated hover:text-admin-ink',
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="flex-1">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="absolute inset-x-0 bottom-0 border-t border-admin-border p-3 bg-admin-surface">
          <div className="flex items-center gap-3 rounded-lg p-2">
            <Avatar name={`${user?.firstName ?? ''} ${user?.surname ?? ''}`.trim() || 'Guest'} size="md" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-admin-ink">
                {user?.firstName} {user?.surname}
              </p>
              <p className="truncate text-xs text-admin-ink-muted capitalize">
                {user?.role.replace('_', ' ')}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              signOut();
              navigate('/');
            }}
            className="mt-1 inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium text-admin-ink-muted hover:bg-admin-elevated hover:text-admin-ink"
          >
            <LogOut className="h-3.5 w-3.5" />
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex flex-1 min-w-0 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-surface/80 px-4 backdrop-blur lg:px-8">
          <button
            className="lg:hidden rounded-lg p-2 text-ink hover:bg-muted"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="flex-1" />
          <RoleSwitcher />
          <NotificationBell to="/admin/dashboard" />
          <ThemeToggle compact />
        </header>
        <main className="flex-1 px-4 py-6 lg:px-8 lg:py-10 max-w-screen-2xl w-full mx-auto">
          <Suspense fallback={<RouteFallback />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
